import { DateOnly } from '@if/core/date'
import { EFinancingProgram, EPaymentPlan, EPlanType } from '@if/core/types'
import { FormContainer, MultiTranslatedError, useFormContainerContext } from '@if/mui'
import { ErrorOutline, Lock } from '@mui/icons-material'
import { Alert, Button, Grid, Stack, Typography, useTheme } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import AvailableCreditTooltip from '@src/components/AvailableCreditTooltip'
import HighEstimateDialog from '@src/components/HighEstimateDialog'
import BaseWorksheetForm, {
  BaseWorksheetFormConfig,
  isPromoPlan,
  useBaseWorksheetFormConfigFromCreditApp,
} from '@src/components/WorksheetForm'
import MerchantPaymentInstructions from '@src/containers/ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import { useMaxPaymentAmountWithAgentLeeway, useMaxPaymentError } from '@src/containers/ViewWorksheet/viewWorksheet-hooks'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import { useUpdatePersonalLoanWorksheet } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useBusinessErrors, useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { useHasCVT } from '@src/data/selectors/credit-selectors'
import {
  AvailableCredit,
  Constants,
  CreditApplicationMessage,
  Merchant,
  PersonalLoanWorksheet,
} from '@src/data/types'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { useUser } from '@src/providers/UserProvider'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PaymentSplitSection from './components/PaymentSplitSection'
import PersonalFundingSummary from './components/PersonalFundingSummary'
import { usePersonalLoanLiveCompute } from './editPersonalLoan-hooks'
import {
  getInitialHighEstimateLock,
  getStoredTermAndPlan,
  PersonalEditSubmitValues,
  PersonalEditValues,
  toEditPersonalLoanWorksheetDto,
  usePersonalEditForm,
  validatePersonalSubmit,
} from './personalEditForm'

const EditPersonalLoanWorksheetPageV2 = () => {
  const { creditApplication, worksheet, isLoading } = useCreditApplicationData()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  if (isLoading || !creditApplication || !merchant || !worksheet) {
    return <PageSpinner isLoading />
  }

  return (
    <PageReady
      creditApplication={creditApplication}
      merchant={merchant}
      worksheet={worksheet as PersonalLoanWorksheet}
    />
  )
}

interface PageReadyProps {
  creditApplication: CreditApplication
  merchant: Merchant
  worksheet: PersonalLoanWorksheet
}

const PageReady = ({ creditApplication, merchant, worksheet }: PageReadyProps) => {
  const navigate = useNavigate()
  const hasCVT = useHasCVT()
  const { t } = useTranslation('global')

  const { config, isLoading: isConfigLoading } = useBaseWorksheetFormConfigFromCreditApp({
    creditApplication,
    merchant,
  })

  const [highEstimateLocked, setHighEstimateLocked] = useState(
    getInitialHighEstimateLock(worksheet, merchant, hasCVT),
  )

  const { schema, defaultValues, isReady } = usePersonalEditForm(config, worksheet)

  const [availableCreditData] = useGetAvailableCredit(creditApplication.financingProgramId, creditApplication.id)
  const [updateWorksheet, isUpdating] = useUpdatePersonalLoanWorksheet()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  // Cross-field rules the schema cannot express (they span the payment rows, the credit decision
  // and the credit report). Thrown as a MultiTranslatedError so FormContainer renders them in the
  // form's own error surface, the way the calculator's compute hook does.
  const onFormSubmitted = async (values: PersonalEditSubmitValues) => {
    const errors = validatePersonalSubmit({
      values,
      merchant,
      creditApplication,
      availableCredit: availableCreditData?.availableAmount ?? 0,
    })
    if (errors.length > 0) {
      throw new MultiTranslatedError(errors.map((e) => ({ key: e.messageKey, options: e.messageArgs })))
    }

    await updateWorksheet(toEditPersonalLoanWorksheetDto(values, worksheet))
    await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
    navigate(`/Applications/${EFinancingProgram.Personal}/${creditApplication.id}/view`, { replace: true })
  }

  if (isConfigLoading || !isReady || !defaultValues) {
    return <PageSpinner isLoading />
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isUpdating} />
      <FormContainer
        useFormSetup={() => ({ schema, defaultValues })}
        mode="create"
        successMessage={t('formSuccess.edit')}
        hideActions
        onSubmit={(values) => onFormSubmitted(values)}
      >
        <PageContent
          creditApplication={creditApplication}
          merchant={merchant}
          worksheet={worksheet}
          config={config}
          availableCreditData={availableCreditData}
          highEstimateLocked={highEstimateLocked}
          onUnlock={() => setHighEstimateLocked(false)}
        />
      </FormContainer>
    </div>
  )
}

interface PageContentProps {
  creditApplication: CreditApplication
  merchant: Merchant
  worksheet: PersonalLoanWorksheet
  config: BaseWorksheetFormConfig
  availableCreditData: AvailableCredit | undefined
  highEstimateLocked: boolean
  onUnlock: () => void
}

const PageContent = ({
  creditApplication,
  merchant,
  worksheet,
  config,
  availableCreditData,
  highEstimateLocked,
  onUnlock,
}: PageContentProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const user = useUser()
  const error = useBusinessErrors()
  const featureFlags = useFeatureFlags()
  const { privateMessages } = useCreditApplicationData()
  const [deleteMessage] = useDeleteMessage()

  const { methods, canSubmit, submit, busy } = useFormContainerContext<PersonalEditValues>()
  const [isHighEstimateDialogOpen, setIsHighEstimateDialogOpen] = useState(false)

  const creditInProgress = availableCreditData?.creditInProgress ?? 0

  const decisionInterestRate = creditApplication.finalCreditDecision?.interestRate ?? 0
  const maxPmtAmount = creditApplication.finalCreditDecision?.maxPmtAmount ?? 0

  const { computedInfo, isComputing, aprCheck } = usePersonalLoanLiveCompute({
    control: methods.control,
    config,
    interestRate: decisionInterestRate,
    insuranceCompanyId: worksheet.insuranceCompanyId,
  })

  const paymentFrequency = methods.watch('paymentFrequency')
  const paymentPlanId = methods.watch('paymentPlanId')
  const isHighEstimate = methods.watch('isHighEstimate')
  const isVariableInterest = isPromoPlan(paymentPlanId)

  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(paymentPlanId as EPaymentPlan, maxPmtAmount)

  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    computedInfo.paymentForFrequency,
    maxPaymentAmount,
    Constants.financingProgramConfigs[EFinancingProgram.Personal].validateMaxPaymentAmountInWorksheet,
  )

  const storedTermAndPlan = useMemo(() => getStoredTermAndPlan(worksheet), [worksheet])

  // Bound the picker to the application's expiry so an out-of-range activation date can't be
  // entered at all — the server owns the rule, this just stops us asking it a question we know
  // the answer to. Memoized: ActivationDate is memo'd and a fresh DateOnly would defeat it.
  const activationMaxDate = useMemo(() => new DateOnly(config.expiresOn), [config.expiresOn])

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.Personal}/${creditApplication.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  // Cross-row and out-of-form rules live in the submit handler (see collectSubmitErrors) so they
  // surface in the form's own error area; here we only intercept for the high-estimate consent.
  const handleSave = async () => {
    if (isHighEstimate) {
      setIsHighEstimateDialogOpen(true)
    } else {
      await submit('save')
    }
  }

  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) =>
    deleteMessage({
      id: message.id,
      creditApplicationId: message.creditApplicationId,
      financingProgramId: creditApplication.financingProgramId,
    })

  const planName = creditApplication.normsCreditDecision?.planName ?? EPlanType.Default

  return (
    <Stack spacing={2}>
      <PageError errors={error} />
      {aprCheck.tooHigh && (
        <Alert severity="warning">
          {t('worksheetCommon.effectiveRateTooHigh', '', {
            effectiveRate: aprCheck.effectiveRate,
            maxRate: aprCheck.threshold,
          })}
        </Alert>
      )}
      <Breadcrumb trees={breadCrumbs} />

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <InputTextField value={t(`EPlanType.${planName}`, { ns: 'enum' })} label={t('worksheetCommon.program')} disabled />
        <InputTextField disabled value={creditApplication.finalCreditDecision?.maxAmountFinanced ?? 0} />
        <AvailableCreditTooltip availableCreditData={availableCreditData}>
          <div style={{ position: 'relative' }}>
            <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
            {creditInProgress !== 0 && (
              <ErrorOutline
                sx={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.palette.error.main,
                  fontSize: '1.5rem',
                }}
              />
            )}
          </div>
        </AvailableCreditTooltip>
        <InputTextField
          label={t('worksheetCommon.availableCredit')}
          disabled
          value={availableCreditData?.availableAmount ?? 0}
        />

        {!highEstimateLocked && (
          <div style={{ marginTop: '1%' }}>
            <Button
              variant="contained"
              disabled={!canSubmit || busy || aprCheck.tooHigh || maxPaymentForFrequencyError !== null}
              sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
              onClick={() => void handleSave()}
            >
              {t('common.save')}
            </Button>
          </div>
        )}
        {highEstimateLocked && (
          <div>
            <Button
              variant="contained"
              disabled={aprCheck.tooHigh || isComputing}
              onClick={onUnlock}
              sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
              startIcon={<Lock />}
            >
              <div>
                <Typography variant="body1" component="div">
                  {t('worksheetCommon.unlockToModify')}
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontSize: '0.75rem' }}>
                  {t('worksheetCommon.willCancelContract')}
                </Typography>
              </div>
            </Button>
          </div>
        )}
      </Stack>

      <PrivateMessage
        messages={privateMessages}
        handleDeletingPrivateMessage={handleDeletingPrivateMessage}
        currentUser={user}
        creditAppCreationDate={creditApplication.createdOn}
      />
      <MerchantPaymentInstructions merchant={merchant} />

      <Grid container marginTop={3} spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <BaseWorksheetForm config={config}>
            <BaseWorksheetForm.AmountRequested control={methods.control} disabled={highEstimateLocked} />
            <BaseWorksheetForm.ActivationDate
              control={methods.control}
              maxDate={activationMaxDate}
              disabled={highEstimateLocked}
            />
            <BaseWorksheetForm.FirstPaymentDate control={methods.control} disabled={highEstimateLocked} />
            <BaseWorksheetForm.PaymentFrequency control={methods.control} locked={highEstimateLocked} />
            <BaseWorksheetForm.Term
              control={methods.control}
              disabled={highEstimateLocked}
              storedTermAndPlan={storedTermAndPlan}
            />
            <BaseWorksheetForm.IncludeProtection control={methods.control} disabled={highEstimateLocked} />
            <BaseWorksheetForm.IsHighEstimate
              control={methods.control}
              disabled={!featureFlags.useHighEstimateOnBuyDown && isVariableInterest}
            />
            <PaymentSplitSection control={methods.control} merchant={merchant} isVariableInterest={isVariableInterest} />
          </BaseWorksheetForm>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PersonalFundingSummary
            control={methods.control}
            computedInfo={computedInfo}
            isComputing={isComputing}
            merchant={merchant}
            decisionInterestRate={decisionInterestRate}
            maxPmtAmount={maxPmtAmount}
          />
        </Grid>
      </Grid>

      <HighEstimateDialog
        open={isHighEstimateDialogOpen}
        title={t('worksheetCommon.saveFinalizeHighEstimateTitle')}
        onConfirm={() => {
          setIsHighEstimateDialogOpen(false)
          void submit('highEstimateConfirm')
        }}
        onCancel={() => setIsHighEstimateDialogOpen(false)}
        text={t('worksheetCommon.saveFinalizeHighEstimateMessage')}
        onChange={(value: boolean) => methods.setValue('highEstimateExplainedToClient', value)}
      />
    </Stack>
  )
}

export default EditPersonalLoanWorksheetPageV2
